import type {Editor, ProjectData} from 'grapesjs';
import type {FieldMeta} from '@FormMakerReact/form-maker.types';
import type {SaveFormResponse} from '@FormMakerReact/lib/form-maker-storage-library';
import type {SaveFormArgs} from '@FormMakerReact/lib/form-maker-storage-library';
import {SaveActionType, SaveType, SignupFormGateBlockedError, loadForm, saveForm} from '@FormMakerReact/lib/form-maker-storage-library';
import {buildFieldsMetaFromCanvas, normalizeLoadedFieldsMeta} from '@FormMakerReact/lib/form-maker-fields-meta-library';
import {noop} from '@Utils/general.util';

const STORAGE_ID = 'form-maker-storage';

export const FIELDS_META_MODEL_KEY = 'formMakerFieldsMeta';

/** Model key the React hook stashes StoreOpts under, so non-React callers (the Ctrl+S keymap) can invoke editor.store(opts) without context access. */
export const STORE_OPTS_MODEL_KEY = 'formMakerStoreOpts';

export interface StoreOpts {
  idEmailSignupForm?: string;
  name?: string;
  fieldsMeta?: FieldMeta[];
  saveActionType?: SaveActionType;
  saveType?: SaveType;
  /**
   * Called with the server's response for the store that just succeeded, plus the SaveType it ran
   * with. The React hook merges the returned fieldsMeta and, for a draft (empty idEmailSignupForm),
   * adopts the id the server minted.
   */
  onSaveResponse?: (response: SaveFormResponse, saveType: SaveType) => void;
}

interface LoadOpts {
  idEmailSignupForm?: string;
}

export function importStorage(editor: Editor): void {
  editor.StorageManager.add(STORAGE_ID, {
    async load(opts?: LoadOpts): Promise<ProjectData> {
      if (!opts?.idEmailSignupForm) {
        return {};
      }
      const loaded = await loadForm(opts.idEmailSignupForm);
      const normalized = normalizeLoadedFieldsMeta(loaded.fieldsMeta);
      editor.getModel().set(FIELDS_META_MODEL_KEY, normalized);
      return loaded.formContent;
    },
    async store(data: ProjectData, opts: StoreOpts): Promise<ProjectData> {
      assertStoreOptsPresent(opts);
      await runSignupFormSaveGate(editor, opts);
      const saveType = opts.saveType ?? SaveType.SOFT;
      const response = await saveForm(buildSaveFormArgs(editor, data, opts, saveType));
      opts.onSaveResponse?.(response, saveType);
      return data;
    },
  });
}

/** A bare editor.store() reaches the adapter as GrapesJS's merged option defaults ({}); without our id that is a caller bug, not a draft save (a draft carries ''). */
function assertStoreOptsPresent(opts: StoreOpts): void {
  if (opts.idEmailSignupForm === undefined) {
    throw new Error('form-maker-storage: store() called without StoreOpts');
  }
}

/**
 * The one gate every save trigger funnels through — React save(), the Ctrl+S command and the embedded
 * Done button all call editor.store(). Throws SignupFormGateBlockedError when the caller may not save
 * yet; the gate's login callback replays the store, whose rejection the storage:* listeners
 * drive. Imported dynamically because the Node headless renderer loads this plugin too and would crash
 * on the gate's browser-only UI import graph.
 */
async function runSignupFormSaveGate(editor: Editor, opts: StoreOpts): Promise<void> {
  const {ensureCanSaveSignupForm} = await import('@FormMakerReact/lib/form-maker-access-library');
  const canSave = ensureCanSaveSignupForm(() => {
    void editor.store(opts).catch(noop);
  });
  if (!canSave) {
    throw new SignupFormGateBlockedError();
  }
}

/** The canvas is authoritative for which fields exist and their order; opts.fieldsMeta only supplies labels and formats, reconciled onto the canvas here. */
function buildSaveFormArgs(editor: Editor, data: ProjectData, opts: StoreOpts, saveType: SaveType): SaveFormArgs {
  const stateFieldsMeta: readonly FieldMeta[] = opts.fieldsMeta ?? (editor.getModel().get(FIELDS_META_MODEL_KEY) as FieldMeta[] | undefined) ?? [];
  return {
    idEmailSignupForm: opts.idEmailSignupForm ?? '',
    name: opts.name ?? '',
    organizationName: '',
    formContent: data,
    htmlContent: editor.getHtml(),
    fieldsMeta: buildFieldsMetaFromCanvas(editor, stateFieldsMeta),
    saveActionType: opts.saveActionType ?? SaveActionType.ATTEMPT,
    saveType,
  };
}
